<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  modelValue: string
  placeholder?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit,
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
  ],
  editorProps: {
    attributes: {
      class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4',
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
})

// Watch for external changes to modelValue
watch(() => props.modelValue, (value) => {
  const isSame = editor.value?.getHTML() === value
  if (!isSame && editor.value) {
    editor.value.commands.setContent(value, false)
  }
})
</script>

<template>
  <div class="border border-gray-300 rounded-lg overflow-hidden bg-white">
    <!-- Toolbar -->
    <div v-if="editor" class="border-b border-gray-300 bg-gray-50 p-2 flex flex-wrap gap-1">
      <!-- Headings -->
      <button
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('heading', { level: 1 }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
      >
        H1
      </button>
      <button
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('heading', { level: 2 }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
      >
        H2
      </button>
      <button
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('heading', { level: 3 }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
      >
        H3
      </button>

      <div class="w-px bg-gray-300 mx-1"></div>

      <!-- Text formatting -->
      <button
        @click="editor.chain().focus().toggleBold().run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('bold') }"
        class="px-3 py-1 text-sm font-bold border border-gray-300 rounded hover:bg-gray-100"
        type="button"
      >
        B
      </button>
      <button
        @click="editor.chain().focus().toggleItalic().run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('italic') }"
        class="px-3 py-1 text-sm font-medium italic border border-gray-300 rounded hover:bg-gray-100"
        type="button"
      >
        I
      </button>
      <button
        @click="editor.chain().focus().toggleUnderline().run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('underline') }"
        class="px-3 py-1 text-sm font-medium underline border border-gray-300 rounded hover:bg-gray-100"
        type="button"
      >
        U
      </button>

      <div class="w-px bg-gray-300 mx-1"></div>

      <!-- Lists -->
      <button
        @click="editor.chain().focus().toggleBulletList().run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('bulletList') }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
        :title="t('wysiwyg.bulletList')"
      >
        •••
      </button>
      <button
        @click="editor.chain().focus().toggleOrderedList().run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive('orderedList') }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
        :title="t('wysiwyg.orderedList')"
      >
        123
      </button>

      <div class="w-px bg-gray-300 mx-1"></div>

      <!-- Text alignment -->
      <button
        @click="editor.chain().focus().setTextAlign('left').run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive({ textAlign: 'left' }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
        :title="t('wysiwyg.alignLeft')"
      >
        ⬅
      </button>
      <button
        @click="editor.chain().focus().setTextAlign('center').run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive({ textAlign: 'center' }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
        :title="t('wysiwyg.alignCenter')"
      >
        ↔
      </button>
      <button
        @click="editor.chain().focus().setTextAlign('right').run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive({ textAlign: 'right' }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
        :title="t('wysiwyg.alignRight')"
      >
        ➡
      </button>
      <button
        @click="editor.chain().focus().setTextAlign('justify').run()"
        :class="{ 'bg-blue-100 border-blue-500': editor.isActive({ textAlign: 'justify' }) }"
        class="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100"
        type="button"
        :title="t('wysiwyg.justify')"
      >
        ⬌
      </button>
    </div>

    <!-- Editor content -->
    <EditorContent :editor="editor" />
  </div>
</template>

<style>
/* TipTap editor styles */
.ProseMirror {
  outline: none;
}

.ProseMirror p.is-editor-empty:first-child::before {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.ProseMirror ul,
.ProseMirror ol {
  padding-left: 1.5rem;
}

.ProseMirror h1 {
  font-size: 2em;
  font-weight: bold;
  margin-top: 0.67em;
  margin-bottom: 0.67em;
}

.ProseMirror h2 {
  font-size: 1.5em;
  font-weight: bold;
  margin-top: 0.83em;
  margin-bottom: 0.83em;
}

.ProseMirror h3 {
  font-size: 1.17em;
  font-weight: bold;
  margin-top: 1em;
  margin-bottom: 1em;
}

.ProseMirror p {
  margin: 0.5em 0;
}

.ProseMirror ul {
  list-style-type: disc;
}

.ProseMirror ol {
  list-style-type: decimal;
}
</style>
